
import { Component, Vue, Prop } from "vue-property-decorator";
import VuePerfectScrollbar from "vue-perfect-scrollbar";

import { VueBootstrapField, UserPermissions } from "@/store/types";
import ClearanceFilterLevelEdit from "@/components/ccure/lockdown/ClearanceFilterLevelEdit.vue";
import { LockdownConfigurationByDesignation } from '@/store/lockdown/types';
import { namespace, Getter } from 'vuex-class';

const Lockdown = namespace("lockdown");

@Component({
	components: {
		"clearance-filter-level-edit": ClearanceFilterLevelEdit,
		"vue-perfect-scrollbar": VuePerfectScrollbar
	}
})
export default class ConfigurationsList extends Vue {
	@Prop({ required: true }) public groupID: number;

	@Lockdown.State(state => state.lockdownConfigurations) private lockdownConfigurations: LockdownConfigurationByDesignation[];
	@Lockdown.Mutation private setConfigurationSelected:(config: LockdownConfigurationByDesignation) => void;
	@Lockdown.Mutation private setConfigurationDeselected:(config: LockdownConfigurationByDesignation) => void;
	@Lockdown.Mutation private setAllConfigurationsSelected:() => void;
	@Lockdown.Mutation private setAllConfigurationsDeselected:() => void;
	@Lockdown.Mutation private setSelectedClearanceFilterLevel:(clearanceLevel: number) => void;
	@Lockdown.Mutation private setUpdateReasonOnLockdownConfiguration:(reason: string) => void;
	@Getter getPermissions: UserPermissions;

	private isEditingConfiguration: boolean = false;
	private selectedConfiguration: LockdownConfigurationByDesignation = null;

	private fields: VueBootstrapField[] = [
		{ key: "multiSelect", label: "" },
		{ key: "isDesignated", label: "Designated", sortable: true, class: "text-center" },
		{ key: "serverTitle", label: "Server", sortable: true },
		{
			key: "clearanceFilterLevel",
			label: "Clearance Filter Level",
			sortable: true,
			class: "text-center"
		}
	];

	public created(): void {
		if (this.lockdownEditPermissionEnabled) {
			this.fields.push({
				key: "actions",
				label: "Actions",
				class: "text-right pr-2"
			});
		}
	}

	public get lockdownEditPermissionEnabled(): boolean {
		return this.getPermissions.canEditCCureLockdown ?? false;
	}

	private get rowsSelected(): LockdownConfigurationByDesignation[] {
		if (!this.lockdownConfigurations) {
			return [];
		}

		return this.lockdownConfigurations.filter((config: LockdownConfigurationByDesignation) => config.selected);
	}

	private get showCheckBox(): boolean {
		return (this.getPermissions.canSelectConfigByCheckbox ?? false) && this.lockdownEditPermissionEnabled;
	}

	private get numberOfRowsSelected(): number {
		return this.rowsSelected.length;
	}

	private rowClass(config: LockdownConfigurationByDesignation): string {
		if (config && config.selected) {
			return "row-selected";
		}

		return "";
	}

	private handleSelectAllClick(checked: boolean): void {
		if (checked) {
			this.setAllConfigurationsSelected();
		} else {
			this.setAllConfigurationsDeselected();
		}
	}

	private handleEditClick(configuration: LockdownConfigurationByDesignation): void {
		if(!this.lockdownEditPermissionEnabled) {
			return;
		}

		this.selectedConfiguration = configuration;
		this.isEditingConfiguration = true;
	}

	private handleMultipleEditClick(): void {
		if(!this.lockdownEditPermissionEnabled) {
			return;
		}
		
		this.selectedConfiguration = null;
		this.isEditingConfiguration = true;
	}

	private handleClearanceFilterEditClosed(): void {
		this.isEditingConfiguration = false;
		this.setSelectedClearanceFilterLevel(null);
		this.setUpdateReasonOnLockdownConfiguration("");
	}

	private badgeClass(item): string {
		switch (item.clearanceFilterLevel) {
			case 1:
			case 2:
				return "success";
			case 3:
			case 4:
				return "warning";
			case 5:
			case 6:
				return "danger";
		}
	}

	private handleItemSelected(lockdownConfiguration: LockdownConfigurationByDesignation): void {
		if (!lockdownConfiguration.selected) {
			this.setConfigurationSelected(lockdownConfiguration);
		} else {
			this.setConfigurationDeselected(lockdownConfiguration);
		}
	}

	private get selectAllStatus(): boolean {
		return this.lockdownConfigurations &&
			this.lockdownConfigurations.length > 0 && 
			this.numberOfRowsSelected === this.lockdownConfigurations.length;
	}
}
